import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { motion } from 'motion/react';
import {
  Wand2,
  Loader2,
  ArrowRight,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  FileText,
  BookOpen,
  Lightbulb } from
'lucide-react';
import { geminiService } from '@/services/geminiService';

interface ContentPlannerProps {
  topic?: string;
  subject?: string;
  gradeLevel?: string;
  numberOfSlides?: number;
  additionalNotes?: string;
  onContentApproved?: (plan: any) => void;
  onBack?: () => void;
  presentationData?: any;
  updatePresentationData?: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isLoading?: boolean;
  setIsLoading?: (loading: boolean) => void;
}

const ContentPlanner: React.FC<ContentPlannerProps> = ({
  topic: propTopic,
  subject: propSubject,
  gradeLevel: propGradeLevel,
  numberOfSlides: propNumberOfSlides,
  additionalNotes: propAdditionalNotes,
  onContentApproved,
  onBack,
  presentationData,
  updatePresentationData,
  onNext,
  onPrevious,
  isLoading,
  setIsLoading
}) => {
  const [formData, setFormData] = useState({
    title: presentationData?.title || '',
    description: presentationData?.description || '',
    topic: propTopic || presentationData?.topic || '',
    subject: propSubject || presentationData?.subject || '',
    gradeLevel: propGradeLevel || presentationData?.gradeLevel || '',
    numberOfSlides: propNumberOfSlides || presentationData?.numberOfSlides || 10,
    additionalNotes: propAdditionalNotes || presentationData?.additionalNotes || '',
    learningObjectives: presentationData?.learningObjectives || '',
    targetAudience: presentationData?.targetAudience || ''
  });

  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('idle'); // idle, generating, completed, error
  const { toast } = useToast();

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generateContent = async () => {
    if (!formData.title || !formData.topic) {
      toast({
        title: "Missing Information",
        description: "Please provide at least a title and topic for your presentation.",
        variant: "destructive"
      });
      return;
    }

    if (setIsLoading) setIsLoading(true);
    setCurrentStep('generating');
    setGenerationProgress(10);

    try {
      console.log('🎨 Generating slides with Gemini AI...');
      setGenerationProgress(30);

      // Use the generateSlides method from geminiService
      const response = await geminiService.generateSlides(
        formData.topic,
        formData.subject,
        formData.gradeLevel,
        formData.numberOfSlides,
        formData.additionalNotes
      );

      setGenerationProgress(70);
      console.log('✅ Slides generated successfully');

      if (response.error) {
        throw new Error(response.error);
      }

      console.log('🔧 Parsing JSON response...');
      // Use the improved JSON parsing method
      const parseResult = geminiService.parseJsonResponse(response.text);
      if (parseResult.error) {
        throw new Error(parseResult.error);
      }

      const slidesData = parseResult.data;
      console.log('✅ JSON parsed successfully');

      setGenerationProgress(90);

      // Validate and process the generated content
      const processedSlides = slidesData.slides?.map((slide: any, index: number) => ({
        id: `slide-${index + 1}`,
        title: slide.title || `Slide ${index + 1}`,
        content: slide.content || 'Content to be added',
        type: slide.type || (index === 0 ? 'title' : index === slidesData.slides.length - 1 ? 'conclusion' : 'content'),
        layout: slide.layout || 'content',
        notes: slide.notes || `Speaker notes for slide ${index + 1}`,
        imageKeywords: slide.imageKeywords || 'education,learning',
        interactiveElement: slide.interactiveElement || ''
      })) || [];

      console.log('📊 Processed slides:', processedSlides.length);

      setGenerationProgress(100);
      setCurrentStep('completed');

      // Update presentation data with the correct structure
      if (updatePresentationData) {
        updatePresentationData({
          title: formData.title,
          description: formData.description,
          subject: formData.subject,
          gradeLevel: formData.gradeLevel,
          topic: formData.topic,
          numberOfSlides: formData.numberOfSlides,
          additionalNotes: formData.additionalNotes,
          learningObjectives: formData.learningObjectives,
          targetAudience: formData.targetAudience,
          content: processedSlides, // This is the key - content should be the slides array
          generatedAt: new Date().toISOString()
        });
      }

      if (onContentApproved) {
        onContentApproved({
          ...formData,
          slides: processedSlides,
          generatedAt: new Date().toISOString()
        });
      }

      toast({
        title: "Content Generated!",
        description: `Successfully generated ${processedSlides.length} slides for "${formData.title}"`
      });

      // Auto-advance to next step after a brief delay
      setTimeout(() => {
        if (onNext) onNext();
      }, 1500);

    } catch (error) {
      console.error('❌ Error generating content:', error);
      setCurrentStep('error');
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate presentation content. Please try again.",
        variant: "destructive"
      });
    } finally {
      if (setIsLoading) setIsLoading(false);
      setTimeout(() => {
        setGenerationProgress(0);
        setCurrentStep('idle');
      }, 3000);
    }
  };

  const canGenerate = formData.title && formData.topic && !isLoading;

  const handlePrevious = () => {
    if (onPrevious) onPrevious();
    if (onBack) onBack();
  };

  return (
    <div className="space-y-6" data-id="rafa0iri0" data-path="src/components/ai/ContentPlanner.tsx">
      {/* Progress indicator */}
      {currentStep === 'generating' &&
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6" data-id="iltn7xtv6" data-path="src/components/ai/ContentPlanner.tsx">
          <Card className="border-blue-200 bg-blue-50" data-id="5sg16e3bk" data-path="src/components/ai/ContentPlanner.tsx">
            <CardContent className="pt-6" data-id="cqe61gjqj" data-path="src/components/ai/ContentPlanner.tsx">
              <div className="flex items-center gap-3 mb-4" data-id="qkd2wlfki" data-path="src/components/ai/ContentPlanner.tsx">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" data-id="r4bdjen11" data-path="src/components/ai/ContentPlanner.tsx" />
                <span className="font-medium text-blue-900" data-id="wv3tao24l" data-path="src/components/ai/ContentPlanner.tsx">Generating presentation content with AI...</span>
              </div>
              <Progress value={generationProgress} className="h-2" data-id="rao8l2o9s" data-path="src/components/ai/ContentPlanner.tsx" />
              <p className="text-sm text-blue-700 mt-2" data-id="eueisydln" data-path="src/components/ai/ContentPlanner.tsx">
                Creating {formData.numberOfSlides} slides about "{formData.topic}"
              </p>
            </CardContent>
          </Card>
        </motion.div>
      }

      {/* Success indicator */}
      {currentStep === 'completed' &&
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="mb-6" data-id="xjdcwijfi" data-path="src/components/ai/ContentPlanner.tsx">
          <Card className="border-green-200 bg-green-50" data-id="qsml9nrxn" data-path="src/components/ai/ContentPlanner.tsx">
            <CardContent className="pt-6" data-id="cejoxqqni" data-path="src/components/ai/ContentPlanner.tsx">
              <div className="flex items-center gap-3" data-id="j0xk27fok" data-path="src/components/ai/ContentPlanner.tsx">
                <CheckCircle className="h-5 w-5 text-green-600" data-id="3g6728iwc" data-path="src/components/ai/ContentPlanner.tsx" />
                <span className="font-medium text-green-900" data-id="6mndku3t3" data-path="src/components/ai/ContentPlanner.tsx">
                  Content generated successfully! Moving to next step...
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      }

      {/* Error indicator */}
      {currentStep === 'error' &&
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6" data-id="6hyxmczvy" data-path="src/components/ai/ContentPlanner.tsx">
          <Card className="border-red-200 bg-red-50" data-id="nmovesvjd" data-path="src/components/ai/ContentPlanner.tsx">
            <CardContent className="pt-6" data-id="k99m7hc6r" data-path="src/components/ai/ContentPlanner.tsx">
              <div className="flex items-center gap-3" data-id="mhqs4cqnt" data-path="src/components/ai/ContentPlanner.tsx">
                <AlertCircle className="h-5 w-5 text-red-600" data-id="3uyfemi07" data-path="src/components/ai/ContentPlanner.tsx" />
                <span className="font-medium text-red-900" data-id="4g3ny2qpg" data-path="src/components/ai/ContentPlanner.tsx">
                  Generation failed. Please check your inputs and try again.
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      }

      {/* Main content form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="molqdrbmj" data-path="src/components/ai/ContentPlanner.tsx">
        {/* Form section */}
        <div className="lg:col-span-2 space-y-6" data-id="fdve6brxh" data-path="src/components/ai/ContentPlanner.tsx">
          <Card data-id="f0r5ysyu9" data-path="src/components/ai/ContentPlanner.tsx">
            <CardHeader data-id="ppbx4dbge" data-path="src/components/ai/ContentPlanner.tsx">
              <CardTitle className="flex items-center gap-2" data-id="4sy705k9e" data-path="src/components/ai/ContentPlanner.tsx">
                <FileText className="h-5 w-5 text-blue-600" data-id="pxifrxqe4" data-path="src/components/ai/ContentPlanner.tsx" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="rbdypqshj" data-path="src/components/ai/ContentPlanner.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="kuip8p6a6" data-path="src/components/ai/ContentPlanner.tsx">
                <div data-id="oiha51hh2" data-path="src/components/ai/ContentPlanner.tsx">
                  <Label htmlFor="title" data-id="3p8rzoy7g" data-path="src/components/ai/ContentPlanner.tsx">Presentation Title *</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Introduction to Photosynthesis"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    className="mt-1" data-id="1pzjcom0a" data-path="src/components/ai/ContentPlanner.tsx" />
                </div>
                <div data-id="o830hthmu" data-path="src/components/ai/ContentPlanner.tsx">
                  <Label htmlFor="topic" data-id="cg3u05tx9" data-path="src/components/ai/ContentPlanner.tsx">Main Topic *</Label>
                  <Input
                    id="topic"
                    placeholder="e.g., Photosynthesis in plants"
                    value={formData.topic}
                    onChange={(e) => handleFormChange('topic', e.target.value)}
                    className="mt-1" data-id="eg8vrugj0" data-path="src/components/ai/ContentPlanner.tsx" />
                </div>
              </div>

              <div data-id="sac5xd0x1" data-path="src/components/ai/ContentPlanner.tsx">
                <Label htmlFor="description" data-id="rlz8t7pfd" data-path="src/components/ai/ContentPlanner.tsx">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Brief description of what this presentation covers..."
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={3}
                  className="mt-1" data-id="rs0rbdt8u" data-path="src/components/ai/ContentPlanner.tsx" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="axbh6nr3e" data-path="src/components/ai/ContentPlanner.tsx">
                <div data-id="nc09hxvxy" data-path="src/components/ai/ContentPlanner.tsx">
                  <Label htmlFor="subject" data-id="nbndedzt5" data-path="src/components/ai/ContentPlanner.tsx">Subject</Label>
                  <Select onValueChange={(value) => handleFormChange('subject', value)} data-id="euee8a5ns" data-path="src/components/ai/ContentPlanner.tsx">
                    <SelectTrigger data-id="e6j1l3zua" data-path="src/components/ai/ContentPlanner.tsx">
                      <SelectValue placeholder="Select subject" data-id="48qfgyisf" data-path="src/components/ai/ContentPlanner.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="hh9d0g93v" data-path="src/components/ai/ContentPlanner.tsx">
                      <SelectItem value="biology" data-id="akw0sexv0" data-path="src/components/ai/ContentPlanner.tsx">Biology</SelectItem>
                      <SelectItem value="chemistry" data-id="a6bjcz3vi" data-path="src/components/ai/ContentPlanner.tsx">Chemistry</SelectItem>
                      <SelectItem value="physics" data-id="lcp7y45bp" data-path="src/components/ai/ContentPlanner.tsx">Physics</SelectItem>
                      <SelectItem value="mathematics" data-id="j2ivkwygt" data-path="src/components/ai/ContentPlanner.tsx">Mathematics</SelectItem>
                      <SelectItem value="english" data-id="nu0lj5emj" data-path="src/components/ai/ContentPlanner.tsx">English</SelectItem>
                      <SelectItem value="history" data-id="rgx8bm6s5" data-path="src/components/ai/ContentPlanner.tsx">History</SelectItem>
                      <SelectItem value="geography" data-id="rjmqmmxgu" data-path="src/components/ai/ContentPlanner.tsx">Geography</SelectItem>
                      <SelectItem value="social-studies" data-id="hnwq82kvq" data-path="src/components/ai/ContentPlanner.tsx">Social Studies</SelectItem>
                      <SelectItem value="computer-science" data-id="w94dbcn6u" data-path="src/components/ai/ContentPlanner.tsx">Computer Science</SelectItem>
                      <SelectItem value="art" data-id="uh0uefaw1" data-path="src/components/ai/ContentPlanner.tsx">Art</SelectItem>
                      <SelectItem value="other" data-id="d92q16s4r" data-path="src/components/ai/ContentPlanner.tsx">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div data-id="s30eoaozm" data-path="src/components/ai/ContentPlanner.tsx">
                  <Label htmlFor="gradeLevel" data-id="li2qafiwg" data-path="src/components/ai/ContentPlanner.tsx">Grade Level</Label>
                  <Input
                    id="gradeLevel"
                    placeholder="e.g., 9th Grade, High School"
                    value={formData.gradeLevel}
                    onChange={(e) => handleFormChange('gradeLevel', e.target.value)}
                    className="mt-1" data-id="wcxicuztr" data-path="src/components/ai/ContentPlanner.tsx" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="7yxp2send" data-path="src/components/ai/ContentPlanner.tsx">
            <CardHeader data-id="z0gwozpth" data-path="src/components/ai/ContentPlanner.tsx">
              <CardTitle className="flex items-center gap-2" data-id="t059gw0m2" data-path="src/components/ai/ContentPlanner.tsx">
                <BookOpen className="h-5 w-5 text-green-600" data-id="wypg7rn26" data-path="src/components/ai/ContentPlanner.tsx" />
                Educational Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="t3x5rcbof" data-path="src/components/ai/ContentPlanner.tsx">
              <div data-id="fh8mshqn0" data-path="src/components/ai/ContentPlanner.tsx">
                <Label htmlFor="learningObjectives" data-id="rldjxbb6s" data-path="src/components/ai/ContentPlanner.tsx">Learning Objectives</Label>
                <Textarea
                  id="learningObjectives"
                  placeholder="What should students learn from this presentation? List the key learning goals..."
                  value={formData.learningObjectives}
                  onChange={(e) => handleFormChange('learningObjectives', e.target.value)}
                  rows={3}
                  className="mt-1" data-id="tcoc2hhcx" data-path="src/components/ai/ContentPlanner.tsx" />
              </div>

              <div data-id="6k9xteq4k" data-path="src/components/ai/ContentPlanner.tsx">
                <Label htmlFor="targetAudience" data-id="d4nrmmg2q" data-path="src/components/ai/ContentPlanner.tsx">Target Audience</Label>
                <Input
                  id="targetAudience"
                  placeholder="e.g., High school students, AP Biology class"
                  value={formData.targetAudience}
                  onChange={(e) => handleFormChange('targetAudience', e.target.value)}
                  className="mt-1" data-id="av3s4zasn" data-path="src/components/ai/ContentPlanner.tsx" />
              </div>

              <div data-id="mkfavruh4" data-path="src/components/ai/ContentPlanner.tsx">
                <Label htmlFor="numberOfSlides" data-id="bnbup18um" data-path="src/components/ai/ContentPlanner.tsx">Number of Slides: {formData.numberOfSlides}</Label>
                <Slider
                  value={[formData.numberOfSlides]}
                  onValueChange={([value]) => handleFormChange('numberOfSlides', value)}
                  max={25}
                  min={5}
                  step={1}
                  className="mt-3" data-id="2ozmcjl9n" data-path="src/components/ai/ContentPlanner.tsx" />
                <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="cr1lmb2xs" data-path="src/components/ai/ContentPlanner.tsx">
                  <span data-id="5sob3oazl" data-path="src/components/ai/ContentPlanner.tsx">5 slides</span>
                  <span data-id="lbdbnxlcn" data-path="src/components/ai/ContentPlanner.tsx">25 slides</span>
                </div>
              </div>

              <div data-id="wd3kbsf5l" data-path="src/components/ai/ContentPlanner.tsx">
                <Label htmlFor="additionalNotes" data-id="fx2xw1pab" data-path="src/components/ai/ContentPlanner.tsx">Additional Notes</Label>
                <Textarea
                  id="additionalNotes"
                  placeholder="Any specific requirements, focus areas, teaching style preferences, or additional context..."
                  value={formData.additionalNotes}
                  onChange={(e) => handleFormChange('additionalNotes', e.target.value)}
                  rows={4}
                  className="mt-1" data-id="y8pdk3foi" data-path="src/components/ai/ContentPlanner.tsx" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview/Info section */}
        <div className="space-y-6" data-id="yi211ijbt" data-path="src/components/ai/ContentPlanner.tsx">
          <Card data-id="0utlnjhso" data-path="src/components/ai/ContentPlanner.tsx">
            <CardHeader data-id="q02jc8iua" data-path="src/components/ai/ContentPlanner.tsx">
              <CardTitle className="flex items-center gap-2" data-id="dr2i585wr" data-path="src/components/ai/ContentPlanner.tsx">
                <Lightbulb className="h-5 w-5 text-yellow-600" data-id="x3p6qwc7x" data-path="src/components/ai/ContentPlanner.tsx" />
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3" data-id="7psrm3s51" data-path="src/components/ai/ContentPlanner.tsx">
              {formData.title &&
              <div data-id="4vanfmrnn" data-path="src/components/ai/ContentPlanner.tsx">
                  <Badge variant="outline" className="text-xs" data-id="bp6qx0vfj" data-path="src/components/ai/ContentPlanner.tsx">Title</Badge>
                  <p className="text-sm mt-1 font-medium" data-id="8wdi5v9yl" data-path="src/components/ai/ContentPlanner.tsx">{formData.title}</p>
                </div>
              }
              
              {formData.topic &&
              <div data-id="x8air5uv5" data-path="src/components/ai/ContentPlanner.tsx">
                  <Badge variant="outline" className="text-xs" data-id="knaojrz0r" data-path="src/components/ai/ContentPlanner.tsx">Topic</Badge>
                  <p className="text-sm mt-1" data-id="ly4tjdg1s" data-path="src/components/ai/ContentPlanner.tsx">{formData.topic}</p>
                </div>
              }

              {formData.subject &&
              <div data-id="h0dnfa0sk" data-path="src/components/ai/ContentPlanner.tsx">
                  <Badge variant="outline" className="text-xs" data-id="qf6f8lf7d" data-path="src/components/ai/ContentPlanner.tsx">Subject</Badge>
                  <p className="text-sm mt-1" data-id="yug3pahzp" data-path="src/components/ai/ContentPlanner.tsx">{formData.subject}</p>
                </div>
              }

              {formData.gradeLevel &&
              <div data-id="kand6ktcz" data-path="src/components/ai/ContentPlanner.tsx">
                  <Badge variant="outline" className="text-xs" data-id="4b5m39cps" data-path="src/components/ai/ContentPlanner.tsx">Grade Level</Badge>
                  <p className="text-sm mt-1" data-id="2vlsd2yqb" data-path="src/components/ai/ContentPlanner.tsx">{formData.gradeLevel}</p>
                </div>
              }

              <div data-id="anj9vij8x" data-path="src/components/ai/ContentPlanner.tsx">
                <Badge variant="outline" className="text-xs" data-id="d55i81cju" data-path="src/components/ai/ContentPlanner.tsx">Slides</Badge>
                <p className="text-sm mt-1" data-id="5tv40ii7u" data-path="src/components/ai/ContentPlanner.tsx">{formData.numberOfSlides} slides</p>
              </div>

              <div className="pt-3 border-t" data-id="s2t62j7zf" data-path="src/components/ai/ContentPlanner.tsx">
                <p className="text-xs text-gray-600" data-id="inhjtcynq" data-path="src/components/ai/ContentPlanner.tsx">
                  AI will generate educational content tailored to your specifications
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Tips card */}
          <Card className="bg-blue-50 border-blue-200" data-id="5gmeorwn7" data-path="src/components/ai/ContentPlanner.tsx">
            <CardHeader data-id="2r75qo3jp" data-path="src/components/ai/ContentPlanner.tsx">
              <CardTitle className="text-sm text-blue-900" data-id="yotas2jdx" data-path="src/components/ai/ContentPlanner.tsx">Tips for Better Results</CardTitle>
            </CardHeader>
            <CardContent data-id="l7j3rjhat" data-path="src/components/ai/ContentPlanner.tsx">
              <ul className="text-xs text-blue-800 space-y-2" data-id="t7xidhas9" data-path="src/components/ai/ContentPlanner.tsx">
                <li data-id="qbfenq8eo" data-path="src/components/ai/ContentPlanner.tsx">• Be specific about your topic and learning objectives</li>
                <li data-id="1nti50l2n" data-path="src/components/ai/ContentPlanner.tsx">• Include the grade level for age-appropriate content</li>
                <li data-id="0aaysvx2p" data-path="src/components/ai/ContentPlanner.tsx">• Mention any specific teaching style preferences</li>
                <li data-id="39bz4rxw6" data-path="src/components/ai/ContentPlanner.tsx">• Add context about your students' background knowledge</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-6 border-t" data-id="z1k51i1v6" data-path="src/components/ai/ContentPlanner.tsx">
        <Button
          onClick={handlePrevious}
          variant="outline"
          className="flex items-center gap-2" data-id="7b0sfu9if" data-path="src/components/ai/ContentPlanner.tsx">
          <ArrowLeft className="h-4 w-4" data-id="1piivfcnb" data-path="src/components/ai/ContentPlanner.tsx" />
          Back to Template
        </Button>

        <Button
          onClick={generateContent}
          disabled={!canGenerate}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" data-id="70j7y3ene" data-path="src/components/ai/ContentPlanner.tsx">
          {isLoading ?
          <>
              <Loader2 className="h-4 w-4 animate-spin" data-id="2m14oo76z" data-path="src/components/ai/ContentPlanner.tsx" />
              Generating...
            </> :
          <>
              <Wand2 className="h-4 w-4" data-id="x3qbxzfhp" data-path="src/components/ai/ContentPlanner.tsx" />
              Generate Content with AI
              <ArrowRight className="h-4 w-4" data-id="pocht12qn" data-path="src/components/ai/ContentPlanner.tsx" />
            </>
          }
        </Button>
      </div>
    </div>);

};

export default ContentPlanner;